import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Container, Row } from 'reactstrap';
import {
    Alignment,
    DateInput,
    Icon,
    InputType,
    Item,
    MultiSelectList,
    NumericInput,
    SelectInput,
    TextArea,
    ToogleSwitch,
} from 'tacho-ui';
import { PaymentStatusEnum } from '../../../core/domainModels/licences/PaymentStatus';
import { getKeyFromEnum } from '../../../core/helpers/enumUtils';
import { ApplicationState } from '../../../state/ApplicationState';
import { DispatchType } from '../../../state/configureStore';
import { fetchLicenceThunk, updateLicenceVersionThunk } from '../../../state/licences/reducer';
import { TachoModal } from '../../components/TachoModal';
import { TachoEditModalTitle } from '../../components/TachoModal/TachoEditTitle';
import config from '../../../config';
import { checkWrongDate, createDateFromTimeZone, dateToStringFormat } from '../../../core/helpers/moment';
import { mapModulesIdsToItems } from '../../pages/modules/services/moduleServices';
import {
    UpdateLicenseVersion,
    defaultUpdateLicenseVersionModel,
} from '../../../core/domainModels/licenceVersions/UpdateLicenseVersion';
import {
    checkVersionOverlapUpdate,
    getDistributorIdForLicenseType,
    isDeveloperLicenseType,
    maxInt,
    modulesChange,
} from './services/licenceService';
import { LicenseTypeEnum } from '../../../core/domainModels/licences/LicenseType';
import useLicenseTypesOptions from '../../hooks/useLicenseTypesOptions';
import { LicenseVersion } from '../../../core/domainModels/licences/LicenseVersion';

interface UpdateLicenceModalProps {
    isOpen: boolean;
    licenseVersion?: LicenseVersion;
    licenseNumber: string;
    onToggle: () => void;
}
export const UpdateLicenceVersionModal = (props: UpdateLicenceModalProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch<DispatchType>();
    const generateLicencePending = useSelector((state: ApplicationState) => state.licences.updateLicencePending);
    const generateLicenceError = useSelector((state: ApplicationState) => state.licences.updateLicenceError);
    const licenceVersionPending = useSelector((state: ApplicationState) => state.licences.licenceVersionPending);
    const licenceVersionError = useSelector((state: ApplicationState) => state.licences.licenceVersionError);
    const [licenceData, setLicenceData] = useState<UpdateLicenseVersion>(defaultUpdateLicenseVersionModel);
    const modules = useSelector((state: ApplicationState) => state.modules.modules);
    const licences = useSelector((state: ApplicationState) => state.licences.licences);
    const distributors = useSelector((state: ApplicationState) => state.distributors.distributors);

    useEffect(() => {
        if (props.licenseVersion) {
            const isNoLimit = props.licenseVersion.maximumNumberOfSupportedCars === maxInt;
            setLicenceData({
                ...defaultUpdateLicenseVersionModel,
                ...props.licenseVersion,
                licenseVersionId: props.licenseVersion.id,
                noCarsLimit: isNoLimit,
                maximumNumberOfSupportedCars: isNoLimit ? 100 : props.licenseVersion.maximumNumberOfSupportedCars,
                distributorId: getDistributorIdForLicenseType(props.licenseVersion.type, props.licenseVersion.distributorId),
            });
        }
    }, [props.isOpen]);
    const onSubmit = () => {
        dispatch(
            updateLicenceVersionThunk({
                updatedLicenceVersion: {
                    ...licenceData,
                    maximumNumberOfSupportedCars: licenceData.noCarsLimit ? maxInt : licenceData.maximumNumberOfSupportedCars,
                    licenseNumber: props.licenseNumber,
                    distributorId: getDistributorIdForLicenseType(licenceData.type, licenceData.distributorId),
                },
                licenseNumber: licenceData.licenseVersionId,
            }),
        )
            .unwrap()
            .then(() => {
                props.onToggle();
                dispatch(fetchLicenceThunk());
            })
            .catch(err => console.log(err));
    };

    const paymentStatusOptions = useMemo(
        (): Array<Item> => [
            { id: PaymentStatusEnum.Awaiting, label: formatMessage({ id: PaymentStatusEnum.Awaiting }) },
            { id: PaymentStatusEnum.Received, label: formatMessage({ id: PaymentStatusEnum.Received }) },
        ],
        [],
    );

    const licenseTypesOptions = useLicenseTypesOptions();

    const distributorsOptions = useMemo((): Array<Item> => {
        return distributors.map(x => ({
            id: x.id,
            label: x.name,
        }));
    }, [distributors]);

    const checkVersionOverlapMemoized = useMemo(() => {
        const licence = licences.find(item => item.licenseNumber === props.licenseNumber);
        if (licence) {
            return checkVersionOverlapUpdate(licence, licenceData);
        }
    }, [props.licenseNumber, licenceData, licences]);

    const onChangeDateFrom = (value: string) => {
        const change = { ...licenceData };
        if (
            !licenceData.validTo ||
            checkWrongDate(licenceData.validTo) ||
            licenceData.validTo < createDateFromTimeZone(value as string).startOf('day')
        )
            change.validTo = createDateFromTimeZone(value as string).endOf('month');
        return setLicenceData({
            ...change,
            validFrom: createDateFromTimeZone(value as string).startOf('day'),
        });
    };
    const onChangeDateTo = (value: string) => {
        const change = { ...licenceData };
        if (
            !licenceData.validFrom ||
            checkWrongDate(licenceData.validFrom) ||
            licenceData.validFrom > createDateFromTimeZone(value as string).endOf('day')
        )
            change.validFrom = createDateFromTimeZone(value).startOf('month');
        return setLicenceData({
            ...change,
            validTo: createDateFromTimeZone(value as string).endOf('day'),
        });
    };
    return (
        <TachoModal
            size={'md'}
            height={'auto'}
            isOpen={props.isOpen}
            onToggle={() => {
                setLicenceData(defaultUpdateLicenseVersionModel);
                props.onToggle();
            }}
            onSubmit={onSubmit}
            title={<TachoEditModalTitle titleToTranslation="licencesPageUpdateLicence" icon={Icon.Edit} />}
            submitLabelToTranslate="commonUpdate"
            submitIcon={Icon.Edit}
            disableSubmitButton={!!checkVersionOverlapMemoized}
            isPending={generateLicencePending || licenceVersionPending}
            errorToTranslate={generateLicenceError || licenceVersionError || checkVersionOverlapMemoized}
        >
            <Container fluid className="d-flex flex-column px-4 gap-2">
                <Row>
                    <ToogleSwitch
                        value={licenceData.noCarsLimit || false}
                        label={formatMessage({ id: 'licencesNoCarsLimit' })}
                        onChange={value =>
                            setLicenceData(prev => ({
                                ...prev,
                                noCarsLimit: value,
                            }))
                        }
                    />
                </Row>
                <Row>
                    <ToogleSwitch
                        value={licenceData.includeBeta || false}
                        label={formatMessage({ id: 'licencesIncludeBeta' })}
                        onChange={value =>
                            setLicenceData({
                                ...licenceData,
                                includeBeta: value,
                            })
                        }
                    />
                </Row>

                <Row>
                    <NumericInput
                        labelLength={5}
                        inputLength={1}
                        min={1}
                        max={100}
                        value={licenceData.maximumNumberOfSupportedCars || 0}
                        label={formatMessage({ id: 'licencesMaximumNumberOfSupportedCars' })}
                        onChange={value => setLicenceData({ ...licenceData, maximumNumberOfSupportedCars: value })}
                        disabled={licenceData.noCarsLimit || generateLicencePending || licenceVersionPending}
                    />
                </Row>
                <Row>
                    <MultiSelectList
                        showSearchBar
                        uncontrolled
                        labelLength={-1}
                        rowsToShow={3}
                        label={formatMessage({ id: 'licencesAllowedModules' })}
                        value={licenceData.allowedModules ? mapModulesIdsToItems(licenceData.allowedModules, modules) : []}
                        selectAllText={formatMessage({ id: 'commonSelectAll' })}
                        placeholder={formatMessage({ id: 'commonSearch' })}
                        selectedLabelsText={formatMessage({ id: 'commonSelected' })}
                        onChange={selectedItems => {
                            setLicenceData(prevState => ({
                                ...licenceData,
                                allowedModules:
                                    modulesChange(selectedItems, prevState)
                                        ?.filter(item => item.checked)
                                        .map(item => ({
                                            tachoModuleId: item.id,
                                            maximumLoggedUsers: (item.additionalValue as number) || 1,
                                        })) || [],
                            }));
                        }}
                        additionalInput={InputType.Numeric}
                        onAdditionalInputChange={selectedItems => {
                            setLicenceData({
                                ...licenceData,
                                allowedModules: selectedItems
                                    .filter(item => item.checked)
                                    .map(item => ({
                                        tachoModuleId: item.id,
                                        maximumLoggedUsers: item.additionalValue as number,
                                    })),
                            });
                        }}
                        disabled={generateLicencePending || licenceVersionPending}
                    />
                </Row>
                <Row>
                    <Col>
                        <DateInput
                            gap={5}
                            locale={config.language}
                            value={dateToStringFormat(licenceData.validFrom)}
                            label={formatMessage({ id: 'licencesValidFrom' })}
                            onChange={onChangeDateFrom}
                            disabled={generateLicencePending || licenceVersionPending}
                            errorMessage={checkVersionOverlapMemoized && formatMessage({ id: checkVersionOverlapMemoized })}
                        />
                    </Col>
                    <Col>
                        <DateInput
                            gap={5}
                            locale={config.language}
                            value={dateToStringFormat(licenceData.validTo)}
                            label={formatMessage({ id: 'licencesValidTo' })}
                            onChange={onChangeDateTo}
                            disabled={generateLicencePending || licenceVersionPending}
                            errorMessage={checkVersionOverlapMemoized && formatMessage({ id: checkVersionOverlapMemoized })}
                        />
                    </Col>
                </Row>
                <Row>
                    <DateInput
                        locale={config.language}
                        value={dateToStringFormat(licenceData.paymentDeadline)}
                        label={formatMessage({ id: 'licencesPaymentDeadline' })}
                        onChange={value => setLicenceData({ ...licenceData, paymentDeadline: createDateFromTimeZone(value) })}
                        disabled={generateLicencePending || licenceVersionPending}
                    />
                </Row>
                <Row>
                    <SelectInput
                        label={formatMessage({ id: 'licencesPaymentStatus' })}
                        value={
                            paymentStatusOptions.find(
                                item => licenceData.paymentStatus && item.id === PaymentStatusEnum[licenceData.paymentStatus],
                            )?.label || ''
                        }
                        options={paymentStatusOptions}
                        onChange={value =>
                            setLicenceData({
                                ...licenceData,
                                paymentStatus: getKeyFromEnum(Object.entries(PaymentStatusEnum), (value as Item).id),
                            })
                        }
                        disabled={generateLicencePending || licenceVersionPending}
                    />
                </Row>
                <Row>
                    <SelectInput
                        label={formatMessage({ id: 'licencesLicenseType' })}
                        value={licenseTypesOptions.find(item => item.id === LicenseTypeEnum[licenceData.type])?.label || ''}
                        options={licenseTypesOptions}
                        onChange={value => {
                            const newType = getKeyFromEnum(Object.entries(LicenseTypeEnum), (value as Item).id);
                            setLicenceData({
                                ...licenceData,
                                type: newType,
                                distributorId: getDistributorIdForLicenseType(newType, licenceData.distributorId),
                            });
                        }}
                    />
                </Row>
                {!isDeveloperLicenseType(licenceData.type) && (
                    <Row>
                        <SelectInput
                            label={formatMessage({ id: 'licencesDistributor' })}
                            value={distributors.find(item => item.id === licenceData.distributorId)?.name || ''}
                            options={distributorsOptions}
                            onChange={value =>
                                setLicenceData({
                                    ...licenceData,
                                    distributorId: (value as Item).id,
                                })
                            }
                        />
                    </Row>
                )}
                <Row>
                    <TextArea
                        align={Alignment.flexStart}
                        value={licenceData.notes}
                        label={formatMessage({ id: 'clientNotes' })}
                        onChange={value => setLicenceData({ ...licenceData, notes: value as string })}
                        labelLength={-1}
                        gap={10}
                    />
                </Row>
            </Container>
        </TachoModal>
    );
};
